<script setup>
/**
 * GoalCard
 * Card component for displaying a single goal with all its details
 */
import { computed } from 'vue'
import GoalProductGallery from './GoalProductGallery.vue'
import GoalAgentTable from './GoalAgentTable.vue'
import AppButton from '../AppButton.vue'
import { formatTimestamp, formatTimestampFull } from '@/core/utils/goalHelpers'

const props = defineProps({
  goal: {
    type: Object,
    required: true,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  highlightAgentId: {
    type: [String, Number],
    default: null,
  },
  showTutorial: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['load-data', 'edit', 'delete', 'agent-click'])

const endDate = computed(() => {
  return formatTimestamp(props.goal.date_end_js)
})

const lastUpdated = computed(() => {
  return formatTimestampFull(props.goal.fetched)
})

function handleLoadData() {
  emit('load-data', props.goal)
}

function handleEdit() {
  emit('edit', props.goal)
}

function handleDelete() {
  emit('delete', props.goal)
}

function handleAgentClick(data) {
  emit('agent-click', data)
}
</script>

<template>
  <div class="goal-card">
    <!-- Header -->
    <div class="goal-header">
      <h3 class="goal-title">{{ goal.title }}</h3>
      <div class="active-indicator" :class="{ active: goal.active }"></div>
    </div>

    <!-- Meta Info -->
    <div class="goal-meta">
      <span class="goal-type-badge">{{ goal.type }}</span>
      <div class="goal-date">
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <span>בתוקף עד: {{ endDate }}</span>
      </div>
    </div>

    <!-- Products Gallery -->
    <GoalProductGallery :products="goal.products" />

    <!-- Agent Table -->
    <div class="goal-table-container">
      <GoalAgentTable
        :goal="goal"
        :highlight-agent-id="highlightAgentId"
        :show-tutorial="showTutorial"
        @agent-click="handleAgentClick"
      />
    </div>

    <!-- Actions (Admin Only) -->
    <div v-if="isAdmin" class="goal-actions">
      <AppButton variant="primary" size="sm" :loading="loading" @click="handleLoadData">
        טען נתונים
      </AppButton>
      <AppButton variant="secondary" size="sm" @click="handleEdit"> ערוך יעד </AppButton>
      <AppButton variant="danger" size="sm" @click="handleDelete"> מחק יעד </AppButton>
    </div>

    <!-- Last Updated -->
    <div class="goal-footer">
      <span class="last-update">עדכון אחרון: {{ lastUpdated }}</span>
    </div>
  </div>
</template>

<style scoped>
.goal-card {
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

/* Header */
.goal-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%);
}

.goal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  text-align: center;
}

.active-indicator {
  position: absolute;
  right: var(--spacing-sm);
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--color-error);
}

.active-indicator.active {
  background: var(--color-success);
}

/* Meta */
.goal-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
}

.goal-type-badge {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-success);
  color: white;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-sm);
  line-height: 1;
}

.goal-date {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.goal-date svg {
  color: var(--text-muted);
}

/* Table Container */
.goal-table-container {
  width: 100%;
  border-top: 1px solid var(--border-color);
}

/* Actions */
.goal-actions {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}

/* Footer */
.goal-footer {
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-tertiary);
  border-top: 1px solid var(--border-color);
}

.last-update {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}
</style>
